import React, { useState } from "react";
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView, 
  TouchableOpacity, 
  TextInput, 
  ActivityIndicator, 
  KeyboardAvoidingView, 
  Platform,
  SafeAreaView,
  Modal,
  FlatList
} from "react-native";
import SvgIcon from '../components/SvgIcon';
import Toast from '../components/Toast';
import { useNavigation, useRoute } from '@react-navigation/native';
import colors from "../theme/colors";
import { useAuth } from "../context/AuthContext";
import { useToast, checkNetworkError } from '../hooks/useToast';

export default function LoginScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { login, logout } = useAuth();
  const { toast, showToast } = useToast();
  
  // Get login type from route params, default to 'mediator'
  const initialLoginType = route.params?.loginType || 'mediator';
  
  const [loginType, setLoginType] = useState(initialLoginType);
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [mobileNumberFocused, setMobileNumberFocused] = useState(false);
  const [countryCode, setCountryCode] = useState('91');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [wrongUserType, setWrongUserType] = useState(null); // 'Mediator' or 'Member'

  const countryCodes = [
    { code: '91', label: '+91 (India)' },
    { code: '1', label: '+1 (USA)' },
    { code: '44', label: '+44 (UK)' },
  ];

  const handleLogin = async () => {
    // Validation
    if (!mobileNumber || !password) {
      setErrorMessage('Please enter mobile number and password');
      return;
    }

    if (mobileNumber.length < 10) {
      setErrorMessage('Please enter a valid mobile number');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setWrongUserType(null);

    try {
      const result = await login(mobileNumber, countryCode, password);
      
      if (result.success) {
        const userData = result.data;
        
        // Check if user type matches selected login type
        const expectedUserType = loginType === 'mediator' ? 'Mediator' : 'Member';
        
        if (userData.userType !== expectedUserType) {
          // User type mismatch - logout immediately and show error
          await logout();
          setWrongUserType(userData.userType);
          setErrorMessage(
            `This account is registered as a ${userData.userType}. ` +
            `You are trying to login as a ${expectedUserType}.`
          );
          return;
        }
        
        // Clear form
        setMobileNumber('');
        setPassword('');
        setRememberMe(false);
        setErrorMessage('');
        
        // Navigate based on user type
        if (userData.userType === 'Mediator') {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Profiles' }],
          });
        } else if (userData.userType === 'Member') {
          navigation.reset({
            index: 0,
            routes: [{ name: 'MyProfile' }],
          });
        } else {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Home' }],
          });
        }
      } else {
        setErrorMessage(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      if (checkNetworkError(error)) {
        showToast('No internet connection. Please check your network and try again.');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Header Section */}
          <View style={[
            styles.headerSection, 
            loginType === 'mediator' ? styles.headerMediator : styles.headerMatchseeker
          ]}>
            <View style={styles.headerRow}>
              <View style={styles.headerSpacer} />
              <Text style={styles.headerTitle}>
                {loginType === 'mediator' ? 'Mediator Login' : 'Match Seeker Login'}
              </Text>
              <TouchableOpacity 
                style={styles.closeButton}
                onPress={handleGoBack}
              >
                <SvgIcon name="close" size={22} color={colors.white} />
              </TouchableOpacity>
            </View>
            <Text style={styles.headerSubtitle}>
              {loginType === 'mediator' 
                ? 'Sign in to access your mediator dashboard.'
                : 'Sign in to access your dashboard.'}
            </Text>
          </View>

          {/* Form Section */}
          <View style={styles.formSection}>
            <Text style={styles.inputLabel}>Mobile Number</Text>
            <View style={styles.mobileInputContainer}>
              <View style={styles.countryCodeButton}>
                <Text style={styles.countryCodeText}>{countryCode}</Text>
              </View>
              <TextInput
                style={[styles.mobileInput, mobileNumberFocused && styles.mobileInputFocused]}
                placeholder="Enter your mobile number"
                placeholderTextColor={colors.textDark}
                value={mobileNumber}
                onChangeText={setMobileNumber}
                keyboardType="phone-pad"
                maxLength={10}
                onFocus={() => setMobileNumberFocused(true)}
                onBlur={() => setMobileNumberFocused(false)}
              />
            </View>

            {/* Country Code Picker Modal */}
            <Modal
              visible={showCountryPicker}
              transparent={true}
              animationType="fade"
              onRequestClose={() => setShowCountryPicker(false)}
            >
              <TouchableOpacity 
                style={styles.pickerModalOverlay}
                activeOpacity={1}
                onPress={() => setShowCountryPicker(false)}
              >
                <View style={styles.pickerModalContent}>
                  <Text style={styles.pickerModalTitle}>Select Country Code</Text>
                  {countryCodes.map((item) => (
                    <TouchableOpacity
                      key={item.code}
                      style={[
                        styles.pickerOption,
                        countryCode === item.code && styles.pickerOptionSelected
                      ]}
                      onPress={() => {
                        setCountryCode(item.code);
                        setShowCountryPicker(false);
                      }}
                    >
                      <Text style={[
                        styles.pickerOptionText,
                        countryCode === item.code && styles.pickerOptionTextSelected
                      ]}>
                        {item.label}
                      </Text>
                      {countryCode === item.code && (
                        <SvgIcon name="checkmark" size={18} color={colors.secondary} />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              </TouchableOpacity>
            </Modal>

            <Text style={styles.inputLabel}>Password</Text>
            <View style={[styles.passwordInputContainer, passwordFocused && styles.passwordInputContainerFocused]}>
              <TextInput
                style={styles.passwordInput}
                placeholder="Enter your password"
                placeholderTextColor={colors.textDark}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <SvgIcon 
                  name={showPassword ? "eye" : "eye-off"} 
                  size={20} 
                  color={colors.textDark} 
                />
              </TouchableOpacity>
            </View>

            <View style={styles.optionsRow}>
              <TouchableOpacity 
                style={styles.rememberMeContainer}
                onPress={() => setRememberMe(!rememberMe)}
              >
                <View style={[styles.checkbox, rememberMe && styles.checkboxChecked]}>
                  {rememberMe && <SvgIcon name="checkmark" size={12} color={colors.white} />}
                </View>
                <Text style={styles.rememberMeText}>Remember me</Text>
              </TouchableOpacity>
              <TouchableOpacity>
                <Text style={styles.forgotPasswordText}>Forgot password?</Text>
              </TouchableOpacity>
            </View>

            {errorMessage ? (
              <View style={styles.errorContainer}>
                <View style={styles.errorContent}>
                  <SvgIcon name="alert-circle" size={16} color={colors.error} />
                  <Text style={styles.errorText}>{errorMessage}</Text>
                </View>
                {wrongUserType && (
                  <TouchableOpacity 
                    style={styles.switchLoginButton}
                    onPress={() => {
                      setLoginType(wrongUserType === 'Mediator' ? 'mediator' : 'matchseeker');
                      setErrorMessage('');
                      setWrongUserType(null);
                    }}
                  >
                    <SvgIcon name="swap-horizontal" size={16} color={colors.white} />
                    <Text style={styles.switchLoginButtonText}>
                      Switch to {wrongUserType} Login
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            ) : null}

            <TouchableOpacity 
              style={[
                styles.signInButton, 
                loginType === 'mediator' ? styles.signInButtonMediator : styles.signInButtonMatchseeker,
                isLoading && styles.signInButtonDisabled
              ]} 
              onPress={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.signInButtonText}>Sign In</Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Bottom Links Section */}
          <View style={[
            styles.bottomLinksContainer,
            loginType === 'mediator' ? styles.bottomLinksMediator : styles.bottomLinksMatchseeker
          ]}>
            <View style={styles.bottomLinks}>
              {loginType === 'mediator' ? (
                <>
                  <Text style={styles.bottomLinkText}>New mediator? </Text>
                  <TouchableOpacity onPress={() => {
                    navigation.push('MediatorRegistration');
                  }}>
                    <Text style={styles.bottomLinkAction}>Apply to become a mediator</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <Text style={styles.bottomLinkText}>Don't have an account? </Text>
                  <TouchableOpacity>
                    <Text style={styles.bottomLinkAction}>Register here</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>

            <View style={styles.bottomLinks}>
              <Text style={styles.bottomLinkText}>
                {loginType === 'mediator' ? 'Looking for a match? ' : 'Are you a mediator? '}
              </Text>
              <TouchableOpacity onPress={() => setLoginType(loginType === 'mediator' ? 'matchseeker' : 'mediator')}>
                <Text style={styles.bottomLinkAction}>
                  {loginType === 'mediator' ? 'Login as match seeker' : 'Login as Mediator'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
      <Toast visible={toast.visible} message={toast.message} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  headerSection: {
    padding: 16,
    borderRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  headerMediator: {
    backgroundColor: '#0D9488', // teal
  },
  headerMatchseeker: {
    backgroundColor: '#0F172A', // slate-900 primary
    borderWidth: 2,
    borderColor: colors.white,
    borderBottomWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  closeButton: {
    padding: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
    marginHorizontal: 10,
  },
  headerSpacer: {
    width: 34,
  },
  headerSubtitle: {
    fontSize: 13,
    color: colors.white,
    lineHeight: 18,
    opacity: 0.9,
    textAlign: 'center',
  },
  formSection: {
    padding: 20,
    backgroundColor: colors.white,
    borderRadius: 12,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary,
    letterSpacing: 0.1,
    marginBottom: 6,
  },
  mobileInputContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
    width: '100%',
  },
  countryCodeButton: {
    width: 80,
    minWidth: 80,
    height: 48,
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    backgroundColor: '#F9FAFB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    flexShrink: 0,
  },
  countryCodeText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  pickerModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  pickerModalContent: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    width: '80%',
    maxWidth: 300,
  },
  pickerModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  pickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  pickerOptionSelected: {
    backgroundColor: '#E6F7F5',
  },
  pickerOptionText: {
    fontSize: 15,
    color: colors.textDark,
  },
  pickerOptionTextSelected: {
    color: colors.secondary,
    fontWeight: '600',
  },
  mobileInput: {
    flex: 1,
    height: 48,
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    paddingHorizontal: 14,
    fontSize: 15,
    backgroundColor: colors.white,
    color: colors.primary,
    minWidth: 0,
  },
  mobileInputFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    paddingHorizontal: 14,
    marginBottom: 16,
    backgroundColor: colors.white,
    height: 48,
  },
  passwordInputContainerFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  passwordInput: {
    flex: 1,
    height: '100%',
    fontSize: 15,
    color: colors.primary,
    outlineWidth: 0,
  },
  optionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: 1.5,
    borderColor: '#D1D5DB',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.secondary,
    borderColor: colors.secondary,
  },
  rememberMeText: {
    fontSize: 13,
    color: colors.primary,
  },
  forgotPasswordText: {
    fontSize: 13,
    color: colors.secondary,
    fontWeight: '600',
  },
  signInButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 4,
  },
  signInButtonMediator: {
    backgroundColor: '#0D9488',
    shadowColor: '#0D9488',
  },
  signInButtonMatchseeker: {
    backgroundColor: '#0F172A',
    shadowColor: '#0F172A',
  },
  signInButtonDisabled: {
    backgroundColor: '#9CA3AF',
    opacity: 0.7,
  },
  signInButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  errorContainer: {
    backgroundColor: '#FEE2E2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    flex: 1,
    color: '#DC2626',
    fontSize: 13,
  },
  switchLoginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#DC2626',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginTop: 10,
  },
  switchLoginButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  bottomLinksContainer: {
    padding: 12,
    paddingVertical: 14,
    borderRadius: 12,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginTop: 0,
  },
  bottomLinksMediator: {
    backgroundColor: '#0D9488',
  },
  bottomLinksMatchseeker: {
    backgroundColor: '#0F172A',
    borderWidth: 2,
    borderColor: colors.white,
  },
  bottomLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  bottomLinkText: {
    fontSize: 13,
    color: colors.white,
  },
  bottomLinkAction: {
    fontSize: 13,
    color: colors.white,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  homeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 20,
    paddingVertical: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 10,
    backgroundColor: colors.white,
  },
  homeButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
});